import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from "react"
import type {
  Campaign,
  FollowUp,
  MessageTemplate,
  CampaignContentContextType,
} from "./types"
import {
  updateFollowUp as apiUpdateFollowUp,
  createFollowUp as apiCreateFollowUp,
  deleteFollowUp as apiDeleteFollowUp,
  duplicateFollowUp as apiDuplicateFollowUp,
  fetchFollowUps as apiFetchFollowUps,
} from "./api"
import { t } from "./i18n"

const AUTOSAVE_DEBOUNCE_MS = 500

interface CampaignContentProviderProps {
  children: ReactNode
  campaign: Campaign
  followUps: FollowUp[]
  editable: boolean
  canAddFollowUp: boolean
  maxFollowUps: number
  messageTemplates: MessageTemplate[]
  allowEmailAttachments: boolean
  allowEmailImages: boolean
  allowVideoLinks: boolean
  currentUserEmail: string
}

const CampaignContentContext = createContext<
  CampaignContentContextType | undefined
>(undefined)

export function CampaignContentProvider({
  children,
  campaign,
  followUps: initialFollowUps,
  editable,
  canAddFollowUp: initialCanAddFollowUp,
  maxFollowUps,
  messageTemplates,
  allowEmailAttachments,
  allowEmailImages,
  allowVideoLinks,
  currentUserEmail,
}: CampaignContentProviderProps) {
  const [followUps, setFollowUps] = useState<FollowUp[]>(initialFollowUps)
  const [selectedStep, setSelectedStep] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup debounce timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [])

  const canAddFollowUp =
    initialCanAddFollowUp && followUps.length < maxFollowUps

  const updateFollowUp = useCallback(
    (step: number, updates: Partial<FollowUp>) => {
      setFollowUps((prev) =>
        prev.map((followUp) =>
          followUp.step === step ? { ...followUp, ...updates } : followUp,
        ),
      )

      setSaveError(null)

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }

      saveTimeoutRef.current = setTimeout(async () => {
        setIsSaving(true)

        const result = await apiUpdateFollowUp({
          campaignId: campaign.id,
          step,
          ...updates,
        })

        setIsSaving(false)

        if (result.success) {
          setLastSavedAt(new Date())
          setSaveError(null)
        } else {
          setSaveError(result.error || t("js.campaigns.content.save_error"))
        }
      }, AUTOSAVE_DEBOUNCE_MS)
    },
    [campaign.id],
  )

  const addFollowUp = useCallback(async () => {
    if (!canAddFollowUp) return

    const newStep = followUps.length
    setIsSaving(true)

    const result = await apiCreateFollowUp({
      campaignId: campaign.id,
      step: newStep,
    })

    if (result.success && result.followUp) {
      setFollowUps((prev) => [...prev, result.followUp])
      setSelectedStep(newStep)
      setLastSavedAt(new Date())
      setSaveError(null)
    } else {
      setSaveError(result.error || t("js.campaigns.content.add_error"))
    }

    setIsSaving(false)
  }, [campaign.id, canAddFollowUp, followUps])

  const deleteFollowUpHandler = useCallback(
    async (step: number) => {
      const followUpToDelete = followUps.find(
        (followUp) => followUp.step === step,
      )
      if (!followUpToDelete?.deletable) return

      setIsSaving(true)

      const result = await apiDeleteFollowUp({
        campaignId: campaign.id,
        step,
      })

      if (result.success) {
        try {
          const updatedFollowUps = await apiFetchFollowUps(campaign.id)
          setFollowUps(updatedFollowUps)

          // Adjust selected step if needed - use functional update to avoid stale closure
          setSelectedStep((prev) => (prev >= step && prev > 0 ? prev - 1 : prev))

          setLastSavedAt(new Date())
          setSaveError(null)
        } catch {
          setSaveError(t("js.campaigns.content.delete_error"))
        }
      } else {
        setSaveError(result.error || t("js.campaigns.content.delete_error"))
      }

      setIsSaving(false)
    },
    [campaign.id, followUps],
  )

  const duplicateFollowUpHandler = useCallback(
    async (step: number) => {
      if (!canAddFollowUp) return

      setIsSaving(true)

      const result = await apiDuplicateFollowUp({
        campaignId: campaign.id,
        step,
      })

      if (result.success && result.followUp) {
        try {
          // Refetch all follow-ups since the backend shifts steps when inserting
          const updatedFollowUps = await apiFetchFollowUps(campaign.id)
          setFollowUps(updatedFollowUps)
          setSelectedStep(result.followUp.step)
          setLastSavedAt(new Date())
          setSaveError(null)
        } catch {
          setSaveError(t("js.campaigns.content.duplicate_error"))
        }
      } else {
        setSaveError(result.error || t("js.campaigns.content.duplicate_error"))
      }

      setIsSaving(false)
    },
    [campaign.id, canAddFollowUp],
  )

  const value: CampaignContentContextType = {
    campaign,
    followUps,
    selectedStep,
    setSelectedStep,
    editable,
    canAddFollowUp,
    maxFollowUps,
    messageTemplates,
    allowEmailAttachments,
    allowEmailImages,
    allowVideoLinks,
    currentUserEmail,
    updateFollowUp,
    addFollowUp,
    deleteFollowUp: deleteFollowUpHandler,
    duplicateFollowUp: duplicateFollowUpHandler,
    isSaving,
    lastSavedAt,
    saveError,
  }

  return (
    <CampaignContentContext.Provider value={value}>
      {children}
    </CampaignContentContext.Provider>
  )
}

export function useCampaignContent() {
  const context = useContext(CampaignContentContext)
  if (context === undefined) {
    throw new Error(
      "useCampaignContent must be used within a CampaignContentProvider",
    )
  }
  return context
}
